import { Controller } from "@hotwired/stimulus"
import { consumeStartIntent } from "../lib/speech/start_intent"
import { Turbo } from "@hotwired/turbo-rails"
import { i18n } from "../lib/i18n"
import { UtteranceMeta, encodeWav, floatTo16BitPcm } from "../lib/speech/audio"
import { SpeechCapture, SpeechCaptureState } from "../lib/speech/capture"
import { pushSpeechLevel, speechLevelTrace } from "../lib/speech/level_trace"
import { UploadContext, UploadQueue, UploadQueueCapacityError } from "../lib/speech/upload_queue"
import { shouldAutoScroll } from "./voice_intake_view"

/**
 * Voice Intake Controller
 *
 * Captures microphone audio for a voice intake session, segments speech into
 * utterances client-side, and POSTs each utterance as a 16 kHz mono WAV chunk
 * to the server, which transcribes it and streams row/proposal updates back
 * over Turbo Streams.
 *
 * Wiring (rendered by the Conversation mode show page ERB, which owns the
 * translated chrome; the capture-pipeline states this controller writes into
 * the pill come from js.voice_intake.* — ADR-0019):
 *   - session target carries data-capture-url / data-chunks-url /
 *     data-finalize-url / data-csrf-token. Rendering the page asks the
 *     server for nothing: the session's capture lease (POST capture) is
 *     requested only when capture is about to start — on arrival armed by
 *     the entry control's one-shot intent, or on the first microphone tap
 *     — so a tab merely viewing the recording never holds the lease. The
 *     one browser holding it may upload audio and finish, and sends the
 *     lease token as a request header on both; the grant also says where
 *     numbering and the session clock continue, and the upload queue is
 *     built from that answer, never from the rendered value, once a grant
 *     exists. A session another tab is recording answers "held": the
 *     microphone stays off, the status line says so, and a Take over
 *     control requests the lease explicitly. The holder renews the lease on
 *     a heartbeat while it holds one; losing it (a takeover elsewhere) stops
 *     capture here the same way.
 *     With the start intent armed (the Conversation-mode control on the
 *     survey page, for this respondent) capture starts at once: that tap is
 *     the gesture, and the browser's permission prompt lands on it the first
 *     time. Any other arrival (a refresh, a link, history) only arms the
 *     page: the pill reads "Tap to start listening" and the first tap starts
 *     capture. A Turbo re-render carrying the same chunks URL must not reset
 *     state or restart a live microphone.
 *   - the pill (togglePause / pillDown / pillUp) toggles listening on a tap;
 *     while paused, pressing and holding it listens only for as long as it is
 *     held (push-to-talk for a noisy room or a moment that should stay off
 *     the record) and returns to paused on release.
 *   - status target receives short state messages.
 *   - currentRow target marks the active question row; each time Turbo swaps
 *     one in, it is scrolled into view unless already visible or the user
 *     scrolled recently (see voice_intake_view.ts).
 *   - togglePause / stop are the dock's actions. Pause is pure client state
 *     (no POST): the ERB styles it via the is-paused class on this element.
 *
 * Shared speech modules own browser capture, segmentation, encoding, and the
 * bounded retry queue. This controller adapts their events to the survey's
 * status, URLs, mic gestures, and Turbo behavior.
 *
 * Broadcast guard: a Turbo Stream that would replace the row whose input the
 * user is typing in is deferred until that input blurs — a proposal landing
 * on the question being answered by hand must never wipe the keystrokes.
 * If the row was replaced by something else meanwhile (the answer's own
 * response), the deferred stream is dropped as stale.
 */

export default class extends Controller<HTMLElement> {
  static targets = ["session", "status", "currentRow", "pill", "finish", "takeOver", "processing"]

  declare readonly hasSessionTarget: boolean
  declare readonly sessionTarget: HTMLElement
  declare readonly hasStatusTarget: boolean
  declare readonly statusTarget: HTMLElement
  declare readonly hasPillTarget: boolean
  declare readonly pillTarget: HTMLButtonElement
  declare readonly hasFinishTarget: boolean
  declare readonly finishTarget: HTMLButtonElement

  private captureUrl = ""
  private chunksUrl = ""
  private finalizeUrl = ""
  private statusUrl = ""
  private csrfToken = ""
  private finalizeResyncTimer: number | null = null
  private finalizeStatusController: AbortController | null = null
  private connected = false
  private stopped = false
  private finalizing = false
  private uploadCapacityExceeded = false
  private paused = false
  // Armed but never started: the ready face of the pill until the first tap.
  private ready = false
  private holdTimer: number | null = null
  private holding = false
  private lastUserScrollMs: number | null = null
  // Row id → the latest stream render held back while staff work in that row.
  private deferredRenders = new Map<string, DeferredRender>()
  // The row whose checkbox group staff are mid-way through picking: set when
  // one of its boxes changes, released by the next pointer or focus outside
  // it. Focus cannot stand in for this — a tap on an option's label ticks the
  // box without focusing it.
  private pickingRowId: string | null = null
  // A real tap on the pill is handled by its pointer events; the click the
  // browser fires afterwards must not toggle a second time. A click with no
  // pointer sequence before it (a screen reader's activation) is the toggle.
  private pointerToggled = false
  private capture: SpeechCapture | null = null
  private uploadQueue: UploadQueue<VoiceChunk> | null = null
  // The capture lease this pipeline uploads under. Built with the pipeline
  // and closed over by its uploads, like the chunks URL: a retired queue
  // keeps its own lease and cannot borrow the next session's.
  private lease: CaptureLease | null = null
  private heartbeatTimer: number | null = null
  // Another tab holds the lease: no microphone here until a takeover.
  private held = false

  // Wheel and touchmove rather than "scroll": our own smooth scrollIntoView
  // fires scroll events, which would read as user scrolls and suppress every
  // subsequent auto-scroll. Only user input produces these two.
  connect() {
    if (isTurboPreview()) return
    this.connected = true
    window.addEventListener("wheel", this.markUserScroll, { passive: true })
    window.addEventListener("touchmove", this.markUserScroll, { passive: true })
    document.addEventListener("turbo:before-stream-render", this.guardFocusedRow)
    this.element.addEventListener("change", this.trackPicking)
    document.addEventListener("pointerdown", this.releasePicking, true)
    document.addEventListener("focusin", this.releasePicking, true)
    // The current row's scroll margin clears the app header, whose height is
    // not fixed; measure it once so the CSS offset is exact.
    const appHeader = document.querySelector<HTMLElement>("body > header")
    if (appHeader) this.element.style.setProperty("--app-header-h", `${Math.round(appHeader.getBoundingClientRect().height)}px`)
  }

  // The session target is server-rendered on the show page, so capture starts
  // when Stimulus connects it on page load. Broadcasts can re-render the
  // region carrying it — an unchanged chunks URL means "same session", which
  // must never reset state (seq!). If the re-render disconnected the target
  // (tearing audio down), restart capture so the sequence continues.
  sessionTargetConnected(el: HTMLElement) {
    if (isTurboPreview()) return
    const chunksUrl = el.dataset.chunksUrl || ""
    if (chunksUrl !== "" && chunksUrl === this.chunksUrl) {
      if (!this.stopped && !this.paused && this.capture?.state !== "listening") void this.startCapture()
      return
    }

    this.retireSpeechPipeline()
    this.chunksUrl = chunksUrl
    this.captureUrl = el.dataset.captureUrl || ""
    this.finalizeUrl = el.dataset.finalizeUrl || ""
    this.statusUrl = el.dataset.statusUrl || ""
    this.csrfToken = el.dataset.csrfToken || ""
    this.stopped = false
    this.finalizing = false
    this.uploadCapacityExceeded = false
    this.clearHeld()
    if (this.hasPillTarget) this.pillTarget.disabled = false
    if (this.hasFinishTarget) this.finishTarget.disabled = false
    this.openSession(Number(el.dataset.nextSeq || 0), chunksUrl, this.csrfToken)
  }

  // Build the pipeline without touching the server: no lease is asked for
  // by a render. The queue is numbered from the rendered value only until a
  // grant replaces it (startCapture), and the lease itself is requested
  // when capture starts — here, if the entry control armed the start intent
  // for this conversation, or on the first tap otherwise. A target without
  // a capture URL (a render from before the lease existed) builds the
  // pipeline as it always did and the server refuses its uploads.
  private openSession(renderedNextSeq: number, chunksUrl: string, csrfToken: string) {
    this.buildSpeechPipeline(renderedNextSeq, chunksUrl, csrfToken, { token: null, clockOffsetMs: 0 })
    if (consumeStartIntent(window.location.pathname)) {
      this.paused = false
      this.ready = false
      this.element.classList.remove("is-paused", "is-ready")
      this.syncPill()
      void this.startCapture()
    } else {
      this.setReady()
    }
  }

  // Staff chose to record here although another tab holds the lease: take
  // it, rebuild the pipeline from the server's numbering (the other tab may
  // have uploaded meanwhile; its clock continues where that left off), and
  // start capture on this tap.
  async takeOver() {
    if (!this.captureUrl || this.stopped) return
    const chunksUrl = this.chunksUrl
    const answer = await this.requestLease(null, true)
    if (!this.connected || this.chunksUrl !== chunksUrl || this.stopped) return
    if (answer.kind !== "granted") {
      if (answer.kind === "held") this.markHeld(answer.heldBy)
      return
    }
    this.retireSpeechPipeline()
    const lease: CaptureLease = { token: answer.token, clockOffsetMs: answer.clockOffsetMs }
    this.buildSpeechPipeline(answer.nextSequence, chunksUrl, this.csrfToken, lease)
    this.clearHeld()
    this.startHeartbeat()
    this.resumeListening()
  }

  // Paused, with the pill's ready face, until staff tap to start.
  private setReady() {
    this.ready = true
    this.paused = true
    this.element.classList.add("is-paused", "is-ready")
    this.syncPill()
  }

  // The pill's accessible name says what it is doing and what a press does;
  // aria-pressed is the listening state. The visible faces are CSS-only, so
  // assistive tech reads these instead.
  private syncPill() {
    if (!this.hasPillTarget) return
    const key = this.ready ? "pill_ready" : this.paused ? "pill_paused" : "pill_listening"
    this.pillTarget.setAttribute("aria-label", i18n.t(`voice_intake.${key}`))
    this.pillTarget.setAttribute("aria-pressed", String(!this.paused))
    if (this.paused) this.setStatus(i18n.t(`voice_intake.${key}`))
  }

  sessionTargetDisconnected() {
    this.capture?.stop({ flush: !this.stopped })
  }

  // A page opened (or reloaded) while the recording is still being processed
  // has no session target and never tapped Finish here, so nothing else would
  // start the status poll that covers a lost Cable subscription. Broadcasts
  // re-render this region at every stage; only one poll loop may run.
  processingTargetConnected(el: HTMLElement) {
    if (!this.statusUrl) this.statusUrl = el.dataset.statusUrl || ""
    if (this.finalizeResyncTimer !== null || this.finalizeStatusController) return
    this.resyncUntilFinalized()
  }

  // Turbo swaps a new current row in as the conversation advances. Follow it —
  // the mic follows attention — unless the row is already fully visible or the
  // user scrolled in the last few seconds (nothing moves under their finger).
  currentRowTargetConnected(el: HTMLElement) {
    if (isTurboPreview()) return
    if (typeof el.scrollIntoView !== "function") return
    // The next question opening below a row staff are still working in must
    // not carry the page away from it.
    if (isTypingIn(document.activeElement) || this.pickingRowId) return
    const rect = el.getBoundingClientRect()
    if (!shouldAutoScroll(rect, window.innerHeight, this.lastUserScrollMs, Date.now())) return
    el.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  disconnect() {
    this.connected = false
    this.stopHeartbeat()
    window.removeEventListener("wheel", this.markUserScroll)
    window.removeEventListener("touchmove", this.markUserScroll)
    document.removeEventListener("turbo:before-stream-render", this.guardFocusedRow)
    this.element.removeEventListener("change", this.trackPicking)
    document.removeEventListener("pointerdown", this.releasePicking, true)
    document.removeEventListener("focusin", this.releasePicking, true)
    this.deferredRenders.clear()
    if (this.finalizeResyncTimer !== null) window.clearTimeout(this.finalizeResyncTimer)
    this.finalizeResyncTimer = null
    this.finalizeStatusController?.abort()
    this.finalizeStatusController = null
    // Leaving the page mid-sentence (the Type answers tab, the app nav):
    // whatever was being said is still an utterance and still uploads —
    // the queue drains before aborting and releasing its retained payloads.
    this.capture?.stop({ flush: !this.stopped })
    this.stopped = true
    const queue = this.uploadQueue
    if (queue) void queue.drain().finally(() => queue.dispose())
  }

  // A confirmed Discard is different from ordinary navigation: none of the
  // buffered speech should follow the session to the server. Turbo fires this
  // only after its confirmation succeeds and before it sends the DELETE.
  discard() {
    this.stopped = true
    this.stopHeartbeat()
    const capture = this.capture
    const queue = this.uploadQueue
    this.capture = null
    this.uploadQueue = null
    this.lease = null
    capture?.stop({ flush: false })
    speechLevelTrace.clear()
    queue?.dispose()
  }

  async stop() {
    if (this.finalizing || this.held) return
    this.finalizing = true
    const finishingUrl = this.finalizeUrl
    const finishingSessionUrl = this.chunksUrl
    const leaseToken = this.lease?.token ?? null
    if (!this.stopped) {
      this.stopped = true
      this.capture?.stop({ flush: true })
      this.paused = true
      this.ready = false
      this.element.classList.add("is-paused")
      this.element.classList.remove("is-ready")
      this.syncPill()
    }
    this.setStatus(i18n.t("voice_intake.processing"))
    // The flushed final utterance posts asynchronously; finalizing before it
    // lands moves the session out of `recording` and the server rejects it.
    await this.uploadQueue?.drain()
    if (this.uploadQueue?.capacityExceeded) {
      this.finalizing = false
      this.markUploadCapacityExceeded()
      return
    }
    // Audio that never reached the server is not "finished": retry it, and
    // if any is still missing, say so and let Finish be tapped again.
    await this.uploadQueue?.retryFailed()
    if (this.uploadQueue?.hasFailures) {
      this.finalizing = false
      this.setStatus(i18n.t("voice_intake.uploads_missing", { count: this.uploadQueue.failureCount }))
      return
    }
    // The finalize response carries the header regions (state + action) as
    // Turbo Streams so this client flips to Processing without waiting on
    // the broadcast round trip.
    const response = await this.postForm(finishingUrl, new FormData(), { returnResponse: true, leaseToken })
    if (!response) {
      this.finalizing = false
      this.setStatus(i18n.t("voice_intake.finish_failed"))
      return
    }
    this.stopHeartbeat()
    const stillShowingFinishingSession =
      this.connected && this.finalizeUrl === finishingUrl && this.chunksUrl === finishingSessionUrl
    if (stillShowingFinishingSession && response.headers.get("content-type")?.includes("turbo-stream")) {
      Turbo.renderStreamMessage(await response.text())
    }
    this.resyncUntilFinalized()
  }

  // Cable normally renders every processing stage. A tab that lost Cable
  // polls only the small status resource, with backoff, and refreshes the
  // document once when processing reaches a terminal state.
  private resyncUntilFinalized(delayMs = FINALIZE_RESYNC_MS) {
    if (!this.connected || !this.statusUrl) return
    this.finalizeResyncTimer = window.setTimeout(() => {
      this.finalizeResyncTimer = null
      const state = document.getElementById("voice_state")
      if (!state || !state.querySelector("[data-processing]")) return
      void this.checkFinalizationStatus(delayMs)
    }, delayMs)
  }

  private async checkFinalizationStatus(delayMs: number) {
    const controller = new AbortController()
    this.finalizeStatusController?.abort()
    this.finalizeStatusController = controller
    // A hung status request must fail like a failed one, or the poll never
    // re-arms and only Cable can recover the terminal state.
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    try {
      const response = await fetch(this.statusUrl, {
        headers: { Accept: "application/json" },
        signal: controller.signal,
      })
      if (!this.connected || this.finalizeStatusController !== controller) return
      if (response.ok) {
        const status = (await response.json()) as { processing?: boolean }
        if (this.connected && this.finalizeStatusController === controller && status.processing === false) {
          Turbo.visit(window.location.href, { action: "replace" })
          return
        }
      }
    } catch {
      // Cable or the next backoff poll can still recover the terminal state.
    } finally {
      window.clearTimeout(timeout)
      if (this.finalizeStatusController === controller) this.finalizeStatusController = null
    }
    this.resyncUntilFinalized(Math.min(delayMs * 2, FINALIZE_RESYNC_MAX_MS))
  }

  // Pause/resume is pure client state — nothing POSTs; the listening pill
  // flips via the is-paused class. Pausing stops the mic tracks and freezes
  // frame processing but keeps seq/clock/upload state, so a resumed capture
  // continues the same chunk sequence. Resume disposes the dead audio graph
  // and goes back through startCapture (which never touches seq).
  togglePause(event?: Event) {
    // Enter/Space on the pill arrive here as keyboard events; stop the
    // synthetic click that would follow and toggle a second time.
    if (event instanceof KeyboardEvent) event.preventDefault()
    if (this.stopped || this.uploadCapacityExceeded) return
    if (this.paused) this.resumeListening()
    else this.pauseListening()
  }

  // Pointer path for the pill: a short press is a tap (toggle); while paused,
  // a press held past HOLD_MS listens only until release.
  pillDown(event: PointerEvent) {
    if (this.stopped || this.uploadCapacityExceeded || event.button !== 0) return
    this.pointerToggled = true
    if (!this.paused) {
      this.pauseListening()
      this.holdTimer = HOLD_CONSUMED
      return
    }
    this.holdTimer = window.setTimeout(() => {
      this.holdTimer = null
      this.holding = true
      this.resumeListening()
    }, HOLD_MS)
  }

  pillUp() {
    if (this.holding) {
      this.holding = false
      this.pauseListening()
      return
    }
    if (this.holdTimer === HOLD_CONSUMED) {
      this.holdTimer = null
      return
    }
    if (this.holdTimer !== null) {
      window.clearTimeout(this.holdTimer)
      this.holdTimer = null
      if (this.paused && !this.stopped) this.resumeListening()
    }
  }

  // Native activation without a pointer sequence — a screen reader, or a
  // click dispatched by assistive tech — toggles like a tap would.
  pillClick() {
    if (this.pointerToggled) {
      this.pointerToggled = false
      return
    }
    this.togglePause()
  }

  // The pointer left or the gesture was cancelled: a hold ends (paused
  // again), a pending tap is forgotten — nothing starts listening.
  pillCancel() {
    this.pointerToggled = false
    if (this.holding) {
      this.holding = false
      this.pauseListening()
      return
    }
    if (this.holdTimer !== null && this.holdTimer !== HOLD_CONSUMED) window.clearTimeout(this.holdTimer)
    this.holdTimer = null
  }

  private resumeListening() {
    if (this.uploadCapacityExceeded) return

    this.paused = false
    this.ready = false
    this.element.classList.remove("is-paused", "is-ready")
    this.syncPill()
    void this.startCapture()
  }

  private pauseListening() {
    if (this.paused) return
    this.paused = true
    this.element.classList.add("is-paused")
    this.syncPill()
    // Whatever was being said when Pause was tapped is still an utterance.
    this.capture?.pause({ flush: true })
    // Nothing is being heard now: the wave says so rather than freezing on
    // the last frames it drew.
    speechLevelTrace.clear()
  }

  // Enter in the clearing's text field: the form's auto-save controller
  // sends the value; blurring the field lets the deferred re-render below
  // apply at once instead of waiting for staff to click elsewhere.
  settleTyping(event: Event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    const autoSave = this.application.getControllerForElementAndIdentifier(form, "auto-save") as { flush?: () => void } | null
    autoSave?.flush?.()
    const active = document.activeElement
    if (active instanceof HTMLElement && form.contains(active)) active.blur()
  }

  // Turbo lets a listener swap the render function for one stream element.
  // A row staff are working in — a text/number/date field with typed
  // content, or a checkbox group mid-pick — is not replaced under them: the
  // render waits until they leave it (focus out of the field; a pointer or
  // focus outside the row for a pick). Every auto-save answers with the row
  // as it stood then, so only the latest deferred render for a row applies,
  // and only if the row is still the same node.
  private guardFocusedRow = (event: Event) => {
    if (isTurboPreview()) return
    const detail = (event as CustomEvent<{ newStream: Element; render: (el: Element) => Promise<void> }>).detail
    const streamElement = detail?.newStream
    if (!streamElement) return
    const targetId = streamElement.getAttribute("target")
    if (!targetId) return
    const row = document.getElementById(targetId)
    if (!row) return

    const active = document.activeElement
    const typing = isTypingIn(active) && row.contains(active)
    const picking = targetId === this.pickingRowId
    if (!typing && !picking) return

    const render = detail.render
    detail.render = () => Promise.resolve()
    const pending = this.deferredRenders.get(targetId)
    if (pending && pending.row === row) {
      pending.streamElement = streamElement
      pending.render = render
      return
    }

    this.deferredRenders.set(targetId, { row, streamElement, render })
    if (!typing) return // a pick is released by releasePicking

    const onFocusOut = (focusEvent: FocusEvent) => {
      const next = focusEvent.relatedTarget
      if (next instanceof Node && row.contains(next)) return
      row.removeEventListener("focusout", onFocusOut)
      this.applyDeferred(targetId)
    }
    row.addEventListener("focusout", onFocusOut)
  }

  private applyDeferred(rowId: string) {
    const entry = this.deferredRenders.get(rowId)
    if (!entry) return
    this.deferredRenders.delete(rowId)
    if (document.getElementById(rowId) === entry.row) void entry.render(entry.streamElement)
  }

  private trackPicking = (event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.type !== "checkbox") return
    const row = target.closest<HTMLElement>("[id^='voice_row_question_']")
    if (row) this.pickingRowId = row.id
  }

  private releasePicking = (event: Event) => {
    const rowId = this.pickingRowId
    if (!rowId) return
    const row = document.getElementById(rowId)
    if (row && event.target instanceof Node && row.contains(event.target)) return
    this.pickingRowId = null
    this.applyDeferred(rowId)
  }

  private markUserScroll = () => {
    this.lastUserScrollMs = Date.now()
  }

  private buildSpeechPipeline(startSequence: number, chunksUrl: string, csrfToken: string, lease: CaptureLease) {
    this.lease = lease
    this.uploadQueue = this.buildUploadQueue(startSequence, chunksUrl, csrfToken, lease)
    this.capture = new SpeechCapture({
      profile: "conversation",
      onUtterance: (samples, meta) => this.enqueueChunk(samples, meta),
      // The room's level, into the trace every wave on the page draws from
      // (the speech-wave controller): the dock's card background, the
      // transcript drawer. The drawing side owns the throttling.
      onLevel: (level) => pushSpeechLevel(level),
      onStateChange: (state) => this.captureStateChanged(state),
    })
  }

  // The queue closes over its session URL, CSRF token, and lease, like the
  // chunks URL: a retired queue keeps its own and cannot borrow the next
  // session's. Capture enqueues through this.uploadQueue, so the queue can
  // be replaced under a live capture.
  private buildUploadQueue(startSequence: number, chunksUrl: string, csrfToken: string, lease: CaptureLease) {
    const queue: UploadQueue<VoiceChunk> = new UploadQueue<VoiceChunk>({
      startSequence,
      concurrency: 2,
      upload: (chunk, context) => this.uploadChunk(chunk, context, chunksUrl, csrfToken, lease),
      onStateChange: (state) => {
        if (this.uploadQueue !== queue || this.stopped) return
        if (queue.capacityExceeded) this.markUploadCapacityExceeded()
        else if (state === "uploading") this.setStatus(i18n.t("voice_intake.uploading"))
        else if (state === "capacity-exceeded") this.markUploadCapacityExceeded()
        else if (state === "failed") this.setStatus(i18n.t("voice_intake.upload_failed"))
        else if (state === "idle" && this.capture?.state === "listening") this.setStatus(i18n.t("voice_intake.listening"))
      },
    })
    return queue
  }

  // A fresh grant is where numbering continues: the queue armed from the
  // rendered data-next-seq may be stale (another holder uploaded meanwhile),
  // so it is replaced — nothing has been enqueued under it yet, but a
  // retired queue is drained all the same before it is disposed.
  private rebuildUploadQueue(startSequence: number, chunksUrl: string, csrfToken: string, lease: CaptureLease) {
    const retired = this.uploadQueue
    this.uploadQueue = this.buildUploadQueue(startSequence, chunksUrl, csrfToken, lease)
    if (retired) void retired.drain().finally(() => retired.dispose())
  }

  private retireSpeechPipeline() {
    const capture = this.capture
    const queue = this.uploadQueue
    this.stopHeartbeat()
    capture?.stop({ flush: true })
    speechLevelTrace.clear()
    this.capture = null
    this.uploadQueue = null
    this.lease = null
    if (queue) void queue.drain().finally(() => queue.dispose())
  }

  // Capture begins here, and so does the lease: a pipeline that has no token
  // yet asks for one first (the intent arrival, the first tap, a tap after
  // a failed request). A grant says where numbering and the session clock
  // continue, and the queue is rebuilt from it; a held answer keeps the
  // microphone off and shows the Take over control.
  private async startCapture() {
    if (this.captureUrl && this.lease && !this.lease.token) {
      const lease = this.lease
      const chunksUrl = this.chunksUrl
      const csrfToken = this.csrfToken
      const answer = await this.requestLease(null, false)
      if (!this.connected || this.lease !== lease || this.stopped) return
      if (answer.kind === "held") {
        this.markHeld(answer.heldBy)
        return
      }
      if (answer.kind !== "granted") {
        this.markCaptureUnavailable(i18n.t("voice_intake.lease_failed"))
        return
      }
      lease.token = answer.token
      lease.clockOffsetMs = answer.clockOffsetMs - (this.capture?.elapsedMs ?? 0)
      this.rebuildUploadQueue(answer.nextSequence, chunksUrl, csrfToken, lease)
      this.startHeartbeat()
    }
    await this.capture?.resume()
  }

  // The holder checks in while it holds a lease — listening or paused — so a
  // quiet room does not read as an abandoned lease; a tab that never
  // acquired one sends nothing. A renewal that comes back "held" means
  // another tab took over: stop here the same way.
  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = window.setInterval(() => void this.heartbeat(), LEASE_HEARTBEAT_MS)
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
  }

  private async heartbeat() {
    const lease = this.lease
    if (!lease?.token || this.stopped || !this.connected) return
    const answer = await this.requestLease(lease.token, false)
    if (this.lease !== lease || this.stopped) return
    if (answer.kind === "held") this.loseLease(answer.heldBy)
  }

  // POST capture: null token asks for (or takes over) the lease, a token
  // renews it. The token goes in a header, never the URL.
  private async requestLease(token: string | null, takeover: boolean): Promise<LeaseAnswer> {
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    try {
      const headers: Record<string, string> = { "X-CSRF-Token": this.csrfToken, Accept: "application/json" }
      if (token) headers[CAPTURE_TOKEN_HEADER] = token
      const body = new FormData()
      if (takeover) body.append("takeover", "1")
      const response = await fetch(this.captureUrl, { method: "POST", headers, body, signal: controller.signal })
      if (response.status === 409) {
        const data = (await response.json()) as { held_by?: string | null }
        return { kind: "held", heldBy: data.held_by ?? null }
      }
      if (!response.ok) return { kind: "failed" }
      const data = (await response.json()) as { token?: string | null; next_sequence?: number; clock_offset_ms?: number }
      return {
        kind: "granted",
        token: data.token ?? token,
        nextSequence: Number(data.next_sequence ?? 0),
        clockOffsetMs: Number(data.clock_offset_ms ?? 0),
      }
    } catch {
      return { kind: "failed" }
    } finally {
      window.clearTimeout(timeout)
    }
  }

  // Another tab holds the lease: the microphone stays off, Finish is not
  // ours to press, and the Take over segment appears.
  private markHeld(heldBy: string | null) {
    this.held = true
    this.stopHeartbeat()
    this.capture?.pause({ flush: false })
    speechLevelTrace.clear()
    this.paused = true
    this.ready = false
    this.element.classList.add("is-paused", "is-held")
    this.element.classList.remove("is-ready")
    this.syncPill()
    if (this.hasPillTarget) this.pillTarget.disabled = true
    if (this.hasFinishTarget) this.finishTarget.disabled = true
    this.setStatus(heldBy ? i18n.t("voice_intake.held_by", { name: heldBy }) : i18n.t("voice_intake.held"))
  }

  private clearHeld() {
    this.held = false
    this.element.classList.remove("is-held")
    if (this.hasPillTarget) this.pillTarget.disabled = this.uploadCapacityExceeded
    if (this.hasFinishTarget) this.finishTarget.disabled = this.uploadCapacityExceeded
  }

  // The server refused this lease (a takeover elsewhere): nothing uploaded
  // from here will be accepted again under it.
  private loseLease(heldBy: string | null) {
    if (this.lease) this.lease.token = null
    this.markHeld(heldBy)
  }

  private captureStateChanged(state: SpeechCaptureState) {
    if (this.stopped) return
    if (state === "requesting") this.setStatus(i18n.t("voice_intake.mic_requesting"))
    else if (state === "listening") this.setStatus(i18n.t("voice_intake.listening"))
    else if (state === "permission-denied") this.markCaptureUnavailable(i18n.t("voice_intake.mic_denied"))
    else if (state === "unsupported") this.markCaptureUnavailable(i18n.t("voice_intake.mic_unsupported"))
    else if (state === "failed") this.markCaptureUnavailable(i18n.t("voice_intake.capture_failed"))
  }

  private markCaptureUnavailable(message: string) {
    this.paused = true
    this.ready = false
    this.element.classList.add("is-paused")
    this.element.classList.remove("is-ready")
    this.syncPill()
    this.setStatus(message)
  }

  private enqueueChunk(samples: Float32Array, meta: UtteranceMeta) {
    const audio = encodeWav(floatTo16BitPcm(samples))
    try {
      this.uploadQueue?.enqueue({ audio, meta }, { bytes: audio.size })
    } catch (error) {
      if (!(error instanceof UploadQueueCapacityError)) throw error
      if (!this.stopped) {
        this.pauseListening()
        this.markUploadCapacityExceeded()
      }
    }
  }

  private async uploadChunk(
    chunk: VoiceChunk,
    { sequence, signal }: UploadContext,
    chunksUrl: string,
    csrfToken: string,
    lease: CaptureLease,
  ): Promise<boolean> {
    const formData = new FormData()
    formData.append("seq", String(sequence))
    // Session-clock ms continue from where the previous holder left off.
    formData.append("started_at_ms", String(Math.round(chunk.meta.startMs + lease.clockOffsetMs)))
    formData.append("ended_at_ms", String(Math.round(chunk.meta.endMs + lease.clockOffsetMs)))
    formData.append("audio", chunk.audio, `chunk_${sequence}.wav`)
    const headers: Record<string, string> = { "X-CSRF-Token": csrfToken, Accept: "text/vnd.turbo-stream.html, text/html" }
    if (lease.token) headers[CAPTURE_TOKEN_HEADER] = lease.token
    const response = await fetch(chunksUrl, { method: "POST", headers, body: formData, signal })
    // 403: not this lease's session any more (another tab took over). Not a
    // network failure — retrying would not change the answer.
    if (response.status === 403 && this.lease === lease && lease.token) this.loseLease(null)
    return response.ok
  }

  // Finalization is not part of the audio upload queue, but gets the same
  // single network retry the previous controller provided — and the same
  // wall-clock cap per attempt, so a hung request fails over instead of
  // leaving the dock on "Finishing…" forever.
  private async postForm(
    url: string,
    body: FormData,
    { leaseToken }: { returnResponse: true; leaseToken?: string | null },
  ): Promise<Response | null> {
    const headers: Record<string, string> = { "X-CSRF-Token": this.csrfToken, Accept: "text/vnd.turbo-stream.html, text/html" }
    if (leaseToken) headers[CAPTURE_TOKEN_HEADER] = leaseToken
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
      try {
        const response = await fetch(url, { method: "POST", headers, body, signal: controller.signal })
        return response.ok ? response : null
      } catch {
        // network error or timeout — loop for the single retry
      } finally {
        window.clearTimeout(timeout)
      }
    }
    return null
  }

  private setStatus(message: string) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  private markUploadCapacityExceeded() {
    this.uploadCapacityExceeded = true
    this.capture?.pause({ flush: false })
    this.paused = true
    this.ready = false
    this.element.classList.add("is-paused")
    this.element.classList.remove("is-ready")
    this.syncPill()
    if (this.hasPillTarget) this.pillTarget.disabled = true
    if (this.hasFinishTarget) this.finishTarget.disabled = true
    this.setStatus(i18n.t("voice_intake.upload_capacity_exceeded"))
  }
}

const FINALIZE_RESYNC_MS = 8000
const FINALIZE_RESYNC_MAX_MS = 60_000
// The capture lease token travels in this header, never in a URL.
const CAPTURE_TOKEN_HEADER = "X-Voice-Capture-Token"
// The server's lease TTL is 45 s (VoiceIntakeSession::CAPTURE_LEASE_TTL):
// a renewal every third of it survives one lost request and a paused tab.
const LEASE_HEARTBEAT_MS = 15_000

interface CaptureLease {
  token: string | null
  // Added to this pipeline's session-clock ms so a new holder's chunks
  // follow the previous holder's instead of restarting at zero.
  clockOffsetMs: number
}

type LeaseAnswer =
  | { kind: "granted"; token: string | null; nextSequence: number; clockOffsetMs: number }
  | { kind: "held"; heldBy: string | null }
  | { kind: "failed" }
// Wall-clock cap on one finalize / status request. Finalize only enqueues the
// job and renders two regions; status is a tiny JSON read — 15 s is far past
// either, and a request still open then is a hang, not a slow answer.
const REQUEST_TIMEOUT_MS = 15_000
// A press on the pill longer than this is a hold (push-to-talk), not a tap.
const HOLD_MS = 350
// Sentinel: the press already acted on pointerdown (it paused); ignore the up.
const HOLD_CONSUMED = -1

// Whether this document has ever had a user gesture — true after the tap on
// Conversation mode that brought staff here (Turbo keeps the document), false
// on a cold load such as a refresh, where browsers would refuse to run audio.
function isTurboPreview(): boolean {
  return document.documentElement.hasAttribute("data-turbo-preview")
}

const NON_TEXT_INPUT_TYPES = new Set(["hidden", "submit", "button", "radio", "checkbox", "file", "range"])

function isTextEntry(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (el instanceof HTMLTextAreaElement) return true
  return el instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(el.type)
}

// A text/number/date field with typed content: staff are mid-way through it.
function isTypingIn(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  return isTextEntry(el) && el.value !== ""
}

interface DeferredRender {
  row: HTMLElement
  streamElement: Element
  render: (el: Element) => Promise<void>
}

interface VoiceChunk {
  audio: Blob
  meta: UtteranceMeta
}
